import { Injectable, NgZone, OnDestroy } from "@angular/core";
import * as _Echo from "laravel-echo";

import Pusher from "pusher-js";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { DatesHelpers } from "../helpers/date";
import { AppService } from "./app.service";
import { LocalStorageService } from "./localStorage.service";
import { MessagesService } from "./messages.service";
import { ProviderService } from "./provider.service";
import { SoundService } from "./sound.service";
import { HapticsService } from "./haptics.service";
import { PusherBackgroundService } from "./pusher-background.service";
import { PushNotificationService } from "./firebase/push-notification.service";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { NetworkService } from "./network.service";
import {
  NotificationSoundDedup,
  NOTIFICATION_SOUND_EVENT_TYPES,
  URGENT_SOUND_EVENT_TYPES,
  STOP_URGENT_SOUND_EVENT_TYPES,
} from "../helpers/notification-sound-dedup";

export type PusherEventSource = "native" | "echo" | "internal";

@Injectable({
  providedIn: "root",
})
export class PusherService implements OnDestroy {
  pusher: any;
  echo!: any;
  roleChannel!: any;
  discussionChannel!: any;
  public notifications: any = [];
  // private dispatcherNewDemandeSubject: Subject<any> = new Subject<any>();
  private dispatcherAcceptDemandeSubject: Subject<any> = new Subject<any>();
  private dispatcherMeetingDemandeSubject: Subject<any> = new Subject<any>();
  private dispatcherMeetingResponseSubject: Subject<any> = new Subject<any>();
  private dispatcherNewNotificationSubject: Subject<any> = new Subject<any>();
  private dispatcherDestinationAttentSubject: Subject<any> = new Subject<any>();
  private dispatcherConfirmProviderArrivalSubject: Subject<any> =
    new Subject<any>();
  private dispatcherAdminCancelDemande: Subject<any> = new Subject<any>();
  private dispatcherProviersPositions: Subject<any> = new Subject<any>();
  private dispatcherDevisCreated: Subject<any> = new Subject<any>();
  private dispatcherDevisStatusChanged: Subject<any> = new Subject<any>();
  private dispatcherImagePreuveUploaded: Subject<any> = new Subject<any>();
  private dispatcherProviderApplyinDevis: Subject<any> = new Subject<any>();
  private dispatcherClientAttentPending: Subject<any> = new Subject<any>();
  private dispatcherDemandeCancelled: Subject<any> = new Subject<any>();
  private dispatcherSignlerProblem: Subject<any> = new Subject<any>();
  private onraodDemandeCanceled: Subject<any> = new Subject<any>();
  private dispatcherUserUpdated: Subject<any> = new Subject<any>();
  private dispatcherDemandeStatusUpdated: Subject<any> = new Subject<any>();
  private dispatcherSignlerProblemProvider: Subject<any> = new Subject<any>();
  private dispatcherReturnOnboarding: Subject<any> = new Subject<any>();
  private popPreAssignedCommand: Subject<any> = new Subject<any>();
  private clientDemandeChanges: Subject<any> = new Subject<any>();
  private demandesDistance: BehaviorSubject<any[]> = new BehaviorSubject<any[]>(
    []
  );

  private dispatcherTransporterStatus: Subject<any> = new Subject<any>();

  private user = this.localstorageService.getUserData();
  role = this.user?.client?.roles?.[0] ?? "";
  id = this.user?.client?.id ?? 0;

  private isReconnecting = false;
  private healInProgress = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectTimeout: any = null;
  private reconnectDebounceTimeout: any = null;
  private foregroundHealDebounceTimeout: any = null;
  private appStateListener: any = null;
  private networkSubscription: any = null;
  private nativePusherEventSubscription: Subscription | null = null;
  private nativeEventProcessedSubscription: Subscription | null = null;
  private hasTriggeredAuthLogout = false;
  /** When true, native layer handles sound in background; JS must not play to avoid double sound. */
  private appInBackground = false;
  private backgroundEnteredAtMs: number | null = null;
  private connectingSinceMs: number | null = null;
  private lastRealtimeActivityAtMs: number | null = null;
  private lastForegroundHealAtMs = 0;

  /** Tracking du dernier changement local de `current_state` via le toggle
   *  prestataire. Sert à protéger contre la race condition UPDATE_USER :
   *  quand le user clique ON, l'API PUT est en cours mais le backend peut
   *  broadcaster UPDATE_USER avec l'ancien state pendant ce temps. Sans
   *  protection, l'app écraserait le current_state local et le toggle
   *  basculerait OFF tout seul.
   *
   *  Levée de la grâce :
   *    - Confirmation reçue : un UPDATE_USER arrive avec
   *      `current_state == requestedState` → on consume le tracking, accepte
   *      le payload, et tout UPDATE_USER ultérieur est autoritaire (cas
   *      légitime admin offline post-confirmation).
   *    - Timeout : LOCAL_STATE_GRACE_PERIOD_MS sans confirmation → on accepte
   *      le payload (le PUT a probablement échoué ou pris trop de temps).
   *    - Logout/disconnect : reset à null par `disconnectAndReset()` pour
   *      ne pas hériter du state précédent sur le prochain user.
   *
   *  Scoping par userId : les UPDATE_USER concernant un autre user.id (ex:
   *  arrivés sur l'ancien channel après changement de session) ne lèvent
   *  pas la grâce courante.
   */
  private localStateChange: {
    userId: number;
    requestedState: boolean;
    startedAtMs: number;
  } | null = null;
  /** Fenêtre max sans confirmation. 10s couvre la latence d'un PUT API +
   *  commit + broadcast retour. Au-delà, on accepte les changements
   *  UPDATE_USER même conflictuels. */
  private static readonly LOCAL_STATE_GRACE_PERIOD_MS = 10_000;
  private static readonly RECONNECT_DEBOUNCE_MS = 500;
  private static readonly CONNECTING_STUCK_MS = 15_000;
  private static readonly FOREGROUND_HEAL_COOLDOWN_MS = 10_000;
  private static readonly REALTIME_INACTIVITY_MS = 60_000;
  /**
   * Normalise un `current_state` reçu en boolean canonique. Le backend
   * Laravel peut envoyer 0/1 (int), true/false (bool), "0"/"1"/"true"/"false"
   * (string) selon les serializers. Sans normalisation, une comparaison
   * stricte `===` échoue (1 !== true, 0 !== false) → la levée à
   * confirmation ne marche pas. Le truthy check ad-hoc échoue aussi
   * (string "0" est truthy en JS).
   *
   * Public static pour être réutilisé par les autres entrées qui
   * persistent l'utilisateur (login, getUserById, etc.) — sinon la
   * normalisation côté UPDATE_USER ne suffit pas car d'autres flows
   * écrasent le current_state local avec une valeur brute backend.
   */
  public static normalizeCurrentState(v: any): boolean {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    if (typeof v === 'string') {
      const lower = v.toLowerCase();
      return lower === 'true' || lower === '1';
    }
    return Boolean(v);
  }

  constructor(
    private localstorageService: LocalStorageService,
    private appService: AppService,
    private messageService: MessagesService,
    private providerService: ProviderService,
    private datesHelpers: DatesHelpers,
    private pushNotificationService: PushNotificationService,
    private soundService: SoundService,
    private hapticsService: HapticsService,
    private pusherBackgroundService: PusherBackgroundService,
    private networkService: NetworkService,
    private ngZone: NgZone
  ) {
    this.setupConnectionMonitoring();
    this.nativePusherEventSubscription = this.pusherBackgroundService
      .nativePusherEventObservable()
      .subscribe((data) => {
        this.computeEvents(data, "native");
      });

    this.nativeEventProcessedSubscription = this.pusherBackgroundService
      .nativeEventProcessedObservable()
      .subscribe((data) => {
        NotificationSoundDedup.markSoundPlayedFromNative(data);
      });
  }

  /**
   * À appeler depuis le toggle UI prestataire (cf `updateStatusPro`) JUSTE
   * AVANT/APRÈS le PUT API. Démarre la fenêtre de grâce qui bloque les
   * UPDATE_USER reçus pendant la latence du PUT pour ne pas écraser le state
   * optimiste local. La grâce est levée dès qu'on reçoit un UPDATE_USER de
   * confirmation (matching `requestedState`), ou après timeout
   * `LOCAL_STATE_GRACE_PERIOD_MS`.
   *
   * @param userId  ID du user concerné (scoping pour éviter de polluer
   *                un autre user après changement de session).
   * @param requestedState  Le state demandé localement (true=ON, false=OFF).
   */
  markLocalCurrentStateChanged(userId: number, requestedState: boolean): void {
    this.localStateChange = {
      userId,
      requestedState,
      startedAtMs: Date.now(),
    };
  }

  /**
   * Résout le `current_state` à persister localement (getUserById, etc.)
   * en appliquant la même fenêtre de grâce anti-race que UPDATE_USER.
   */
  resolveCurrentStateForPersistence(userId: number, incomingState: any): boolean {
    const normalizedIncoming = PusherService.normalizeCurrentState(incomingState);
    const lsc = this.localStateChange;
    if (!lsc || lsc.userId !== userId) {
      return normalizedIncoming;
    }

    const elapsed = Date.now() - lsc.startedAtMs;
    const requestedState = PusherService.normalizeCurrentState(lsc.requestedState);
    if (normalizedIncoming === requestedState) {
      this.localStateChange = null;
      return normalizedIncoming;
    }
    if (elapsed < PusherService.LOCAL_STATE_GRACE_PERIOD_MS) {
      return requestedState;
    }

    this.localStateChange = null;
    return normalizedIncoming;
  }

  /**
   * Coupe Pusher/Echo et libère les références pour que la prochaine connexion
   * utilise le JWT actuel (obligatoire après logout / changement de session).
   */
  disconnectAndReset(options?: { preserveReconnectLock?: boolean }): void {
    try {
      this.unsubscribeChannels();
    } catch (e) {
      console.warn("[PusherService] unsubscribe during reset:", e);
    }
    try {
      const pusher = this.getPusherClient();
      if (pusher?.disconnect) {
        pusher.disconnect();
      }
    } catch (e) {
      console.warn("[PusherService] disconnect error:", e);
    }
    this.echo = null as any;
    this.pusher = null;
    this.roleChannel = null as any;
    this.discussionChannel = null as any;
    this.connectingSinceMs = null;
    if (!options?.preserveReconnectLock) {
      this.isReconnecting = false;
      this.healInProgress = false;
      this.reconnectAttempts = 0;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.reconnectDebounceTimeout) {
      clearTimeout(this.reconnectDebounceTimeout);
      this.reconnectDebounceTimeout = null;
    }
    if (Capacitor.isNativePlatform() && this.pusherBackgroundService.isAvailable()) {
      this.pusherBackgroundService.stopListening().catch(() => { });
    }
    this.hasTriggeredAuthLogout = false;
    // Reset le tracking anti-race UPDATE_USER : sans ça, un re-login dans
    // la fenêtre de 10s pourrait hériter du current_state demandé par
    // l'utilisateur précédent et l'appliquer au nouveau user → bug subtil.
    this.localStateChange = null;
    PusherService.clearProcessedEventCache();
  }

  auth(): Observable<any> {
    return new Observable((observer) => {
      const user = this.localstorageService.getUserData();
      if (this.echo) {
        this.disconnectAndReset({ preserveReconnectLock: this.healInProgress });
      }
      const accessToken = this.localstorageService.getToken();
      let options: any = {
        ...environment.pusher,
        auth: {
          headers: {
            Authorization: "Bearer " + accessToken,
          },
        },
        // Mobile-specific options for better connection stability
        enabledTransports: ['ws', 'wss'],
        disabledTransports: [],
        cluster: environment.pusher.cluster,
        forceTLS: environment.pusher.forceTLS,
        // Enable activity timeout for mobile (keeps connection alive)
        activityTimeout: 30000, // 30 seconds
        // Enable pong timeout for mobile
        pongTimeout: 6000, // 6 seconds
        // Enable unavailable timeout
        unavailableTimeout: 10000, // 10 seconds
        // Enable encrypted connection
        encrypted: true,
      };

      if (user.client) {
        const pusherClient = new Pusher(options.key, options);

        // Set up connection state listeners
        this.setupPusherConnectionListeners(pusherClient);

        this.echo = new _Echo.default({
          ...options,
          client: pusherClient,
        });

        // Start native background Pusher listening if available (skip prestataire OFF)
        if (
          Capacitor.isNativePlatform() &&
          this.pusherBackgroundService.isAvailable() &&
          this.shouldMaintainRealtimeSubscriptions()
        ) {
          this.pusherBackgroundService.startListening().catch((error) => {
            console.warn('[PusherService] Failed to start native background listening:', error);
          });
        }

        observer.next(this.echo);
        observer.complete();
      } else {
        // Pas connecté / données user absentes : ne pas lever une erreur (évite un bruit console).
        // Les appelants testent déjà la présence de `res` avant de s'abonner aux channels.
        observer.next(null);
        observer.complete();
      }
    });
  }

  private setupPusherConnectionListeners(pusherClient: any): void {
    if (!pusherClient) return;

    pusherClient.connection.bind('state_change', (states: any) => {
      if (states.current === 'connecting') {
        if (!this.connectingSinceMs) {
          this.connectingSinceMs = Date.now();
        }
      } else {
        this.connectingSinceMs = null;
      }

      if (['disconnected', 'failed', 'unavailable'].includes(states.current)) {
        this.handleDisconnection();
      } else if (states.current === 'connected') {
        this.handleReconnection(false);
      }
    });

    pusherClient.connection.bind('error', (error: any) => {
      console.error('Pusher connection error:', error);
      this.handleDisconnection();
    });

    pusherClient.connection.bind('connected', () => {
      this.handleReconnection(false);
    });
  }

  private setupConnectionMonitoring(): void {
    try {
      if (this.networkService) {
        this.networkSubscription = this.networkService.getNetworkStatus().subscribe((isConnected: boolean) => {
          if (isConnected) {
            this.reconnectAttempts = 0;
            if (this.echo && this.isRealtimeUnhealthy()) {
              this.scheduleReconnectPusherDebounced();
            }
          }
        });
      }
    } catch (error) {
      console.warn('Could not set up network monitoring for Pusher:', error);
    }

    if (Capacitor.isNativePlatform()) {
      try {
        this.appStateListener = App.addListener('appStateChange', (state: any) => {
          if (state.isActive) {
            const wasInBackground = this.appInBackground;
            this.appInBackground = false;
            this.scheduleForegroundHeal(wasInBackground);
          } else {
            this.appInBackground = true;
            this.backgroundEnteredAtMs = Date.now();
          }
        });
      } catch (error) {
        console.warn('Could not set up app state monitoring for Pusher:', error);
      }
    }
  }

  /** Public wrapper for periodic health checks (NavigationService timer). */
  isRealtimeUnhealthy(): boolean {
    return this.needsReconnect('network');
  }

  /**
   * Heal realtime connection after foreground resume or periodic health check.
   */
  async ensureRealtimeConnectionOnForeground(wasInBackground = false): Promise<void> {
    if (!this.shouldMaintainRealtimeSubscriptions()) {
      this.logReconnectDiag('foreground heal skipped: no realtime subscriptions');
      return;
    }

    const now = Date.now();
    if (now - this.lastForegroundHealAtMs < PusherService.FOREGROUND_HEAL_COOLDOWN_MS) {
      return;
    }

    this.reconnectAttempts = 0;
    await this.pusherBackgroundService.syncNativeReadyStatus();

    if (this.needsReconnect('foreground', wasInBackground)) {
      this.lastForegroundHealAtMs = now;
      this.logReconnectDiag('foreground force heal', { wasInBackground });
      this.forceHealConnection();
      return;
    }

    if (this.isTransportConnected()) {
      this.lastForegroundHealAtMs = now;
      this.logReconnectDiag('foreground re-subscribe only');
      this.ensureChannelSubscriptions(true);
    }
  }

  shouldMaintainRealtimeSubscriptions(): boolean {
    const data = this.localstorageService.getUserData();
    const client = data?.client;
    if (!client) {
      return false;
    }

    const role = String(client.roles?.[0] ?? "").toLowerCase();
    const isPrestataire = role === "prestataire" || role === "provider";
    if (isPrestataire) {
      return PusherService.normalizeCurrentState(client.current_state) === true;
    }
    return true;
  }

  ensureChannelSubscriptions(force = false): void {
    if (!this.shouldMaintainRealtimeSubscriptions() || !this.echo) {
      return;
    }

    if (force) {
      this.unsubscribeChannels();
      this.subscribeToRoleChannel();
      this.subscribeToDiscussionChannel();
      return;
    }

    if (!this.roleChannel) {
      this.subscribeToRoleChannel();
    }
    if (!this.discussionChannel) {
      this.subscribeToDiscussionChannel();
    }
  }

  private getPusherClient(): any {
    if (!this.echo) {
      return null;
    }
    return (
      this.echo.connector?.pusher ||
      this.echo.pusher ||
      (this.echo as any).client
    );
  }

  private getConnectionState(): string | null {
    try {
      const pusher = this.getPusherClient();
      return pusher?.connection?.state ?? null;
    } catch {
      return null;
    }
  }

  private isTransportConnected(): boolean {
    return this.getConnectionState() === 'connected';
  }

  private needsReconnect(
    reason: 'foreground' | 'network' | 'state_change' = 'network',
    wasInBackground = false
  ): boolean {
    if (!this.shouldMaintainRealtimeSubscriptions()) {
      return false;
    }

    if (!this.echo) {
      return true;
    }

    const state = this.getConnectionState();
    if (!state) {
      return true;
    }

    if (['disconnected', 'failed', 'unavailable'].includes(state)) {
      return true;
    }

    if (
      state === 'connecting' &&
      this.connectingSinceMs != null &&
      Date.now() - this.connectingSinceMs > PusherService.CONNECTING_STUCK_MS
    ) {
      return true;
    }

    if (reason === 'foreground' && wasInBackground && Capacitor.isNativePlatform()) {
      return true;
    }

    if (reason === 'foreground' && wasInBackground && this.backgroundEnteredAtMs != null) {
      const backgroundDuration = Date.now() - this.backgroundEnteredAtMs;
      const inactiveForMs =
        this.lastRealtimeActivityAtMs == null
          ? backgroundDuration
          : Date.now() - this.lastRealtimeActivityAtMs;
      if (
        backgroundDuration > PusherService.REALTIME_INACTIVITY_MS &&
        inactiveForMs > PusherService.REALTIME_INACTIVITY_MS
      ) {
        return true;
      }
    }

    return false;
  }

  private touchRealtimeActivity(): void {
    this.lastRealtimeActivityAtMs = Date.now();
  }

  private logReconnectDiag(message: string, extra?: Record<string, unknown>): void {
    if (environment.production) {
      return;
    }
    console.log('[PusherService]', message, {
      state: this.getConnectionState(),
      reconnectAttempts: this.reconnectAttempts,
      nativeReady: this.pusherBackgroundService.isNativeReady(),
      ...extra,
    });
  }

  private scheduleForegroundHeal(wasInBackground: boolean): void {
    if (this.foregroundHealDebounceTimeout) {
      clearTimeout(this.foregroundHealDebounceTimeout);
    }
    this.foregroundHealDebounceTimeout = setTimeout(() => {
      this.foregroundHealDebounceTimeout = null;
      void this.ensureRealtimeConnectionOnForeground(wasInBackground);
    }, PusherService.RECONNECT_DEBOUNCE_MS);
  }

  private handleDisconnection(): void {
    if (!this.shouldMaintainRealtimeSubscriptions()) {
      return;
    }
    if (this.isReconnecting) {
      return;
    }
    this.isReconnecting = true;
    this.reconnectAttempts = 0;
    this.scheduleReconnect();
  }

  private handleReconnection(forceResubscribe = false): void {
    this.healInProgress = false;
    this.isReconnecting = false;
    this.reconnectAttempts = 0;
    this.connectingSinceMs = null;
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.ensureChannelSubscriptions(forceResubscribe);
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn(
        '[PusherService] max attempts reached, will retry on next foreground/network'
      );
      this.isReconnecting = false;
      this.healInProgress = false;
      return;
    }

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);

    this.reconnectTimeout = setTimeout(() => {
      this.reconnectPusherImmediate();
    }, delay);
  }

  /** Coalesce les déclencheurs réseau / state_change en une seule reconnexion. */
  private scheduleReconnectPusherDebounced(): void {
    if (this.reconnectDebounceTimeout) {
      clearTimeout(this.reconnectDebounceTimeout);
    }
    this.reconnectDebounceTimeout = setTimeout(() => {
      this.reconnectDebounceTimeout = null;
      this.reconnectPusherImmediate();
    }, PusherService.RECONNECT_DEBOUNCE_MS);
  }

  private forceHealConnection(): void {
    if (!this.shouldMaintainRealtimeSubscriptions()) {
      this.logReconnectDiag('force heal skipped: no realtime subscriptions');
      return;
    }

    if (!this.echo) {
      this.healInProgress = true;
      this.isReconnecting = true;
      this.auth().subscribe({
        next: (res: any) => {
          if (res) {
            setTimeout(() => this.handleReconnection(true), 1000);
          } else {
            this.healInProgress = false;
            this.isReconnecting = false;
          }
        },
        error: () => {
          this.healInProgress = false;
          this.isReconnecting = false;
        },
      });
      return;
    }

    this.performAuthRebuild(true);
  }

  private performAuthRebuild(forceResubscribe: boolean): void {
    if (!this.shouldMaintainRealtimeSubscriptions()) {
      this.healInProgress = false;
      this.isReconnecting = false;
      return;
    }

    this.healInProgress = true;
    this.isReconnecting = true;

    try {
      const pusher = this.getPusherClient();
      if (pusher?.disconnect) {
        pusher.disconnect();
      }
    } catch (e) {
      console.warn('[PusherService] Error disconnecting Pusher before rebuild:', e);
    }

    setTimeout(() => {
      this.auth().subscribe({
        next: (res: any) => {
          if (res) {
            setTimeout(() => {
              this.handleReconnection(forceResubscribe);
            }, 1000);
          } else {
            this.healInProgress = false;
            this.scheduleReconnect();
          }
        },
        error: (error) => {
          console.error('[PusherService] Pusher reconnection error:', error);
          this.healInProgress = false;
          this.scheduleReconnect();
        },
      });
    }, 500);
  }

  private reconnectPusherImmediate(options?: { force?: boolean }): void {
    const force = options?.force ?? false;

    if (!this.echo) {
      if (!force) {
        this.isReconnecting = false;
        return;
      }
      this.forceHealConnection();
      return;
    }

    if (!this.shouldMaintainRealtimeSubscriptions()) {
      this.isReconnecting = false;
      return;
    }

    this.reconnectAttempts++;

    if (!force && this.isTransportConnected() && !this.needsReconnect('network')) {
      this.handleReconnection(false);
      return;
    }

    this.performAuthRebuild(force);
  }

  private handlePusherAuthError(error: any): void {
    const isAuthError = error?.type === "AuthError";
    const isForbidden = Number(error?.status) === 403;

    if (isAuthError && isForbidden && !this.hasTriggeredAuthLogout) {
      this.hasTriggeredAuthLogout = true;
      this.disconnectAndReset();
      // Live Activity iOS : terminer la carte lockscreen — sinon elle reste
      // affichée jusqu'au staleDate (8h) avec une session déconnectée. Appel
      // ici ciblé (pas dans disconnectAndReset qui sert aussi à la reconnexion
      // au changement de JWT, où la LA doit rester active).
      this.pusherBackgroundService.endLiveActivity().catch(() => { });
      this.localstorageService.logout();
    }
  }
  subscribeToRoleChannel() {
    if (!this.echo) {
      console.warn('Cannot subscribe to role channel: Echo not initialized');
      return;
    }

    let user = this.localstorageService.getUserData();
    let role = user?.client?.roles?.[0] ?? "";
    let id = user?.client?.id ?? 0;

    if (!user?.client || !role || !id) {
      console.warn('Cannot subscribe to role channel: Invalid user data');
      return;
    }

    const roleChannel = "private-." + role + "." + id;

    try {
      if (this.roleChannel) {
        this.unsubscribeFromRoleChannel();
      }

      this.roleChannel = this.echo.channel(roleChannel);

      // Listen for subscription success
      this.roleChannel.subscribed(() => {
      });

      // Listen for subscription errors
      this.roleChannel.error((error: any) => {
        console.error('Error subscribing to role channel:', error);
        this.handlePusherAuthError(error);
      });

      // Listen for events
      this.roleChannel.listen(".action", (event: any) => {
        this.computeEvents(event, "echo");
      });

    } catch (error) {
      console.error('Error setting up role channel subscription:', error);
    }
  }

  subscribeToDiscussionChannel() {
    if (!this.echo) {
      console.warn('Cannot subscribe to discussion channel: Echo not initialized');
      return;
    }

    let user = this.localstorageService.getUserData();
    let id = user?.client?.id ?? 0;

    if (!user?.client || !id) {
      console.warn('Cannot subscribe to discussion channel: Invalid user data');
      return;
    }

    const discussionChannel = `private-.discussion.${id}`;

    try {
      if (this.discussionChannel) {
        this.unsubscribeFromDiscussionChannel();
      }

      this.discussionChannel = this.echo.channel(discussionChannel);

      // Listen for subscription success
      this.discussionChannel.subscribed(() => {
      });

      // Listen for subscription errors
      this.discussionChannel.error((error: any) => {
        console.error('Error subscribing to discussion channel:', error);
        this.handlePusherAuthError(error);
      });

      // Listen for events
      this.discussionChannel.listen(".action", (event: any) => {
        this.computeMessagesEvents(event, "echo");
      });
    } catch (error) {
      console.error("Error setting up discussion channel subscription:", error);
    }
  }
  unsubscribeFromDiscussionChannel() {
    if (this.discussionChannel) {
      const user = this.localstorageService.getUserData();
      const id = user?.client?.id ?? 0;
      const discussionChannel = `private-.discussion.${id}`;

      this.echo.leave(discussionChannel); // Leaves the channel
      this.discussionChannel = null; // Clear the reference
    }
  }
  unsubscribeFromRoleChannel() {
    if (this.roleChannel) {
      const user = this.localstorageService.getUserData();
      let role = user?.client?.roles?.[0] ?? "";
      const id = user?.client?.id ?? 0;
      const roleChannel = "private-." + role + "." + id;
      this.echo.leave(roleChannel); // Leaves the channel
      this.roleChannel = null; // Clear the reference
    }
  }
  unsubscribeChannels() {
    if (this.discussionChannel) {
      this.unsubscribeFromDiscussionChannel();
      this.discussionChannel = null;
    }
    if (this.roleChannel) {
      this.unsubscribeFromRoleChannel();
    }
  }

  /**
   * Cache event_id → timestamp ms pour dédup TTL 60s côté JS. Miroir de la
   * dédup native (PusherBackgroundManager.recentEventIds iOS / SoundEffectsHelper.recentEventIds
   * Android). Sans ça, double-son observé runtime quand l'app foregrounde :
   *   1. App background : natif joue son via WebSocket Pusher natif. event_id
   *      mis en cache natif TTL 60s.
   *   2. App foreground : JS Pusher Echo reconnecte et reçoit le même event
   *      (replay Pusher OU event encore en transit). Le JS handler appelle
   *      sendVibrate() et dispatcher.next(data).
   *   3. sendVibrate() check `nativeReady` mais peut être false transitoirement
   *      pendant la reconnexion → JS plays sound → DOUBLE-SON.
   *   4. Même sans son, le dispatcher.next(data) re-déclenche les handlers UI
   *      (toast, modale, etc.) ce qui apparaît comme une "nouvelle notif".
   *
   * Avec cette dédup, un event reçu 2x dans la fenêtre de 60s est totalement
   * ignoré la 2e fois (pas de sendVibrate, pas de dispatcher.next).
   */
  /** Cache dédup UI uniquement (distinct du cache sonore). */
  private static recentEventIds = new Map<string, number>();
  /** TTL session-long pour survivre aux coupures réseau > 60s (scénario reconnect). */
  private static readonly EVENT_ID_TTL_MS = 15 * 60_000;

  static clearProcessedEventCache(): void {
    PusherService.recentEventIds.clear();
    NotificationSoundDedup.clear();
  }

  /** @deprecated Utiliser NotificationSoundDedup.markSoundPlayedFromNative */
  static markEventProcessedFromNative(data: Record<string, any>): void {
    NotificationSoundDedup.markSoundPlayedFromNative(data);
  }

  private static buildUiDedupKey(data: any): string | null {
    const eventId = NotificationSoundDedup.normalizeEventId(data?.event_id);
    if (eventId) {
      return `id:${eventId}`;
    }
    const type =
      data?.type_event ??
      data?.event_type ??
      data?.type_notification ??
      data?.event;
    if (!type || !NOTIFICATION_SOUND_EVENT_TYPES.has(type)) {
      return null;
    }
    const demandRef =
      data?.demande ??
      data?.demande_id ??
      data?.demand_id ??
      data?.id_demande ??
      data?.id;
    if (demandRef == null) {
      return null;
    }
    return `fb:${type}:${demandRef}`;
  }

  /**
   * Retourne true si l'event a déjà été traité dans la fenêtre TTL → skip.
   * Sinon enregistre et retourne false (premier traitement).
   */
  private isDuplicateEvent(data: any): boolean {
    const key = PusherService.buildUiDedupKey(data);
    if (!key) {
      return false;
    }
    return PusherService.registerDedupKey(key);
  }

  /** Enregistre la clé ; retourne true si doublon (déjà vu dans le TTL). */
  private static registerDedupKey(key: string): boolean {
    const now = Date.now();
    const ttl = PusherService.EVENT_ID_TTL_MS;
    if (PusherService.recentEventIds.size > 200) {
      for (const [k, ts] of PusherService.recentEventIds) {
        if (now - ts > ttl) {
          PusherService.recentEventIds.delete(k);
        }
      }
    }
    const seenAt = PusherService.recentEventIds.get(key);
    if (seenAt != null && now - seenAt < ttl) {
      return true;
    }
    PusherService.recentEventIds.set(key, now);
    return false;
  }

  private shouldIgnoreEchoWhenNativeReady(source: PusherEventSource): boolean {
    return (
      source === "echo" &&
      Capacitor.isNativePlatform() &&
      this.pusherBackgroundService.isNativeReady()
    );
  }

  private resolveEventType(data: any): string | undefined {
    return data?.type_event ?? data?.event_type ?? data?.type_notification ?? data?.event;
  }

  private resolveDemandReferenceFromEvent(data: any): any {
    return (
      data?.demande ??
      data?.demande_id ??
      data?.demand_id ??
      data?.id_demande ??
      data?.id
    );
  }

  private summarizeRealtimeData(data: any): Record<string, any> {
    const demande = data?.demande;
    const demandeObject = demande && typeof demande === "object" ? demande : null;

    return {
      type_event: this.resolveEventType(data),
      event_id: data?.event_id,
      demande_id:
        data?.demande_id ??
        data?.demand_id ??
        data?.id_demande ??
        demandeObject?.id ??
        (typeof demande === "number" || typeof demande === "string" ? demande : data?.id),
      statut: data?.statut ?? demandeObject?.statut,
      keys: data && typeof data === "object" ? Object.keys(data) : [],
    };
  }

  private buildDemandEventPayload(data: any): any {
    const demandRef = this.resolveDemandReferenceFromEvent(data);
    const payload = { ...data };
    if (payload.demande == null && demandRef != null) {
      payload.demande = demandRef;
    }
    if (
      payload.demande_id == null &&
      (typeof demandRef === "number" || typeof demandRef === "string")
    ) {
      payload.demande_id = demandRef;
    }
    return payload;
  }

  computeEvents(data: any, source: PusherEventSource = "internal") {
    // Pusher/Echo callbacks fire outside Angular's zone → wrap so that all
    // Subject.next() calls inside trigger change detection immediately.
    this.ngZone.run(() => this._computeEvents(data, source));
  }

  private _computeEvents(data: any, source: PusherEventSource) {
    if (this.shouldIgnoreEchoWhenNativeReady(source)) {
      return;
    }

    const rtType = this.resolveEventType(data);
    if (this.isDuplicateEvent(data)) {
      return;
    }
    this.touchRealtimeActivity();
    switch (rtType) {
      case "REQUEST_NEW_FCM":
        this.pushNotificationService.initFcmPusher({ forceRefresh: true });
        break;
      case "POP_PRE_ASSIGN_COMMAND":
        this.soundService.stopUrgentAlert();
        this.pusherBackgroundService.stopUrgentSound();
        this.sendVibrate(data);
        this.appService.notifyDemandRetractionFeedback(data);
        this.popPreAssignedCommand.next(data);
        break;
      case "CLIENT_DEMANDE_CHANGES": {
        const changeStatut = data?.demande?.statut ?? data?.demande?.status;
        if (changeStatut === "CANCELED" || changeStatut === "FINISHED") {
          this.sendVibrate(data);
        }
        this.clientDemandeChanges.next(data);
        break;
      }
      case "MEET_CONFIRMATION":
        this.dispatcherMeetingResponseSubject.next(data);
        break;
      case "SEND_MEET_CLIENT":
        this.dispatcherMeetingDemandeSubject.next(data);
        break;
      case "NEW_PRE_ASSIGN_DEMANDE":
      case "NEW_DEMANDE":
        this.sendUrgentAlert(data);
        {
          const payload = this.buildDemandEventPayload(data);
          this.providerService.storeAndDemandData(payload).catch((error) => {
          });
        }
        break;
      case "ADMIN_MESSAGE":
        this.appService.refreshHomeDataStats();
        this.messageService.realtimeClientMessage.next(data);
        this.messageService.setUnreadMessages();
        this.sendVibrate(data);
        break;
      case "ASSIGNED_DEMANDE_CLIENT":
        this.dispatcherAcceptDemandeSubject.next(data);
        this.dispatcherNewNotificationSubject.next(data);
        this.sendVibrate(data);

        break;
      case "DESTINATION_ATTENT_CLIENT":
        this.sendVibrate(data);
        this.dispatcherDestinationAttentSubject.next(data);
        break;
      case "CONFIRM_ARRIVAL_PRESTATAIRE":
        this.sendVibrate(data);
        this.dispatcherConfirmProviderArrivalSubject.next(data);
        break;
      case "TRACK_POSITION_PROVIDER":
        this.dispatcherProviersPositions.next(data);
        break;
      case "CREATION_DEVIS_CLIENT":
        this.dispatcherDevisCreated.next(data);
        this.sendVibrate(data);
        break;
      case "CHANGE_STATUT_DEVIS":
        this.sendVibrate(data);
        this.dispatcherDevisStatusChanged.next(data);
        break;
      case "SEND_IMAGEPREUVE_CLIENT":
        this.dispatcherImagePreuveUploaded.next(data);
        break;
      case "APPLY_PROVIDER_DEVIS":
        this.sendVibrate(data);
        this.dispatcherProviderApplyinDevis.next(data);
        break;
      case "ATTENT_PENDING_PRESTATAIRE":
        this.dispatcherClientAttentPending.next(data);
        break;
      case "CANCELED_PRESTATAIRE_DEMANDE":
        this.soundService.stopUrgentAlert();
        this.pusherBackgroundService.stopUrgentSound();
        this.sendVibrate(data);
        this.dispatcherDemandeCancelled.next(data);
        break;
      case "SIGNAL_PROBLEM_CLIENT":
        this.dispatcherSignlerProblem.next(data);
        break;
      case "ONROAD_DEMANDE_CANCELED":
        this.soundService.stopUrgentAlert();
        this.pusherBackgroundService.stopUrgentSound();
        this.sendVibrate(data);
        this.onraodDemandeCanceled.next(data);
        break;
      case "UPDATE_USER":
        let user = data.data;
        let token = this.localstorageService.getToken();
        // Anti-race UPDATE_USER vs toggle ON/OFF local. 4 cas :
        //   1. Pas de toggle local en cours → accepte le payload tel quel.
        //   2. Toggle local pour user.id différent (ex: changement session)
        //      → accepte le payload (le tracking est obsolète).
        //   3. UPDATE_USER avec current_state == requestedState → c'est la
        //      confirmation backend du PUT, on lève la grâce et accepte.
        //      Tout UPDATE_USER ultérieur sera autoritaire (cas admin offline).
        //   4. UPDATE_USER avec current_state != requestedState pendant la
        //      fenêtre → broadcast stale du backend (race), on préserve local.
        //   5. UPDATE_USER avec current_state != requestedState après expiration
        //      → on accepte (admin override post-fenêtre).
        const lsc = this.localStateChange;
        if (lsc && lsc.userId === user?.id) {
          const elapsed = Date.now() - lsc.startedAtMs;
          // Normaliser des deux côtés : backend envoie 0/1 ou true/false
          // selon les serializers. Sans ça, 1 !== true → confirmation jamais
          // détectée → grâce reste active jusqu'au timeout, l'admin override
          // pendant la fenêtre est ignoré (régression).
          const payloadState = PusherService.normalizeCurrentState(user.current_state);
          const requestedState = PusherService.normalizeCurrentState(lsc.requestedState);
          if (payloadState === requestedState) {
            // Cas 3 : confirmation reçue, consume + accept
            console.log(`[PusherService] UPDATE_USER confirme current_state=${payloadState} (grâce levée)`);
            this.localStateChange = null;
          } else if (elapsed < PusherService.LOCAL_STATE_GRACE_PERIOD_MS) {
            // Cas 4 : conflit pendant grâce → préserve local (race probable)
            console.log(`[PusherService] UPDATE_USER conflit pendant grâce (${elapsed}ms), preserve current_state=${requestedState}`);
            user.current_state = requestedState;
          } else {
            // Cas 5 : grâce expirée, accept serveur
            console.log(`[PusherService] UPDATE_USER conflit après expiration (${elapsed}ms), accept current_state=${payloadState}`);
            this.localStateChange = null;
          }
        }
        // Cas 1 et 2 : tombent ici sans modif (lsc null ou userId mismatch).
        // Normaliser current_state en boolean canonique AVANT persistence,
        // pour les cas 1/2/3/5 où la valeur backend brute n'a pas été
        // remplacée. Sans ça, le payload pourrait contenir "0"/"1" (string)
        // qui sont TRUTHY en JS — un test `if (user.current_state)` retournerait
        // true même pour "0" → le provider apparaîtrait ON alors que le
        // backend dit OFF. Le cas 4 a déjà set `user.current_state = requestedState`
        // (boolean), donc la normalisation est idempotente.
        if (typeof user?.current_state !== 'undefined') {
          user.current_state = PusherService.normalizeCurrentState(user.current_state);
        }
        this.localstorageService.saveAuthData(token || "", user);
        break;
      case "INFORM_PRESTATAIRE_DEMANDE":
        this.sendUrgentAlert(data);
        this.dispatcherDemandeStatusUpdated.next(data);
        break;
      case "SIGNAL_PROBLEM_PROVIDER":
        this.sendVibrate(data);
        this.dispatcherSignlerProblemProvider.next(data);
        break;
      case "ADMIN_CANCELED_DEMANDE":
        this.soundService.stopUrgentAlert();
        this.pusherBackgroundService.stopUrgentSound();
        this.sendVibrate(data);
        this.dispatcherAdminCancelDemande.next(data);
        break;
      case "ONBOARDING_SUCCESS":
        this.dispatcherReturnOnboarding.next(data);
        break;
      case "UPDATE_TRANSPORT_STATUS":
        this.dispatcherTransporterStatus.next(data);
        break;
      case "SEND_CLIENT_PROVIDER_POSITION":
        const distancesArray = this.demandesDistance.value;
        const dataValue = data.data;

        if (!!distancesArray) {
          const existingIndex = distancesArray.findIndex(
            (item) => item.demande_id === dataValue.demande_id
          );
          if (existingIndex !== -1) {
            distancesArray[existingIndex] = {
              ...distancesArray[existingIndex],
              ...dataValue,
            };
          } else {
            distancesArray.push(dataValue);
          }
          this.demandesDistance.next(distancesArray); // Emit updated array
        }
        break;
      default:
        break;
    }
    //TODO
    // this.sendVibrate();
  }

  dispatchDistance(distance: any) {
    if (distance && distance.distance) {
      this.demandesDistance.next([distance]);
    }
  }
  /**
   * Trigger haptic feedback, sound, and vibration when Pusher receives a message.
   *
   * Coordination anti-double-son :
   * - Sur Capacitor native AVEC service natif PRÊT (`nativeReady` = abonné à au
   *   moins un channel via subscription_succeeded) : skip. Le natif joue le son
   *   lui-même via `triggerNotificationEffects()` (allowlist + dédup, foreground
   *   et background). Évite le trou foreground/background.
   * - Sur Capacitor native AVEC service natif NON démarré (Android 12+ refuse
   *   startForegroundService) ou DÉMARRÉ MAIS PAS ENCORE ABONNÉ : on tombe sur
   *   le fallback JS pour ne pas être silencieux. Sans ça, fenêtre de silence
   *   entre `startListening()` et l'arrivée de `subscription_succeeded`
   *   (régression GPT-5.5 review #3).
   * - Sur Web (PWA) : on joue via JS Howler/HTML5 Audio (pas de natif).
   */
  async sendVibrate(eventData?: Record<string, unknown>) {
    if (Capacitor.isNativePlatform() && this.pusherBackgroundService.isNativeReady()) {
      return;
    }
    if (NotificationSoundDedup.shouldSkipSound(eventData)) {
      return;
    }
    if (Capacitor.isNativePlatform()) {
      console.warn("[PusherService] Native pusher not ready (started but not yet subscribed, or stopped) → JS fallback for sound");
    }
    this.hapticsService.light();
    await this.soundService.playNotificationSound(eventData);
  }

  /**
   * Déclenche l'alerte urgente loopée (nouvelle intervention prestataire).
   * Sur Capacitor native avec nativeReady, le natif gère la sonnerie urgente.
   * En fallback JS (web/PWA ou natif non-ready), on joue via Howler.js.
   */
  async sendUrgentAlert(eventData?: Record<string, unknown>) {
    if (Capacitor.isNativePlatform() && this.pusherBackgroundService.isNativeReady()) {
      // Le natif (PusherBackgroundService / PusherBackgroundManager) gère le son urgent.
      return;
    }
    if (NotificationSoundDedup.shouldSkipSound(eventData)) {
      return;
    }
    if (Capacitor.isNativePlatform()) {
      console.warn("[PusherService] Native pusher not ready → JS fallback for urgent alert");
    }
    NotificationSoundDedup.markSoundPlayed(eventData);
    await this.soundService.playUrgentAlert();
  }
  computeMessagesEvents(data: any, source: PusherEventSource = "echo") {
    this.ngZone.run(() => this._computeMessagesEvents(data, source));
  }

  private _computeMessagesEvents(data: any, source: PusherEventSource) {
    if (this.shouldIgnoreEchoWhenNativeReady(source)) {
      return;
    }
    if (this.isDuplicateEvent(data)) {
      return;
    }
    this.touchRealtimeActivity();
    switch (data.type_event) {
      case "DISCUSSION":
        this.sendVibrate(data);
        this.appService.refreshHomeDataStats();
        this.messageService.addConversations(data);
        this.messageService.realtimeClientMessage.next(data);
        this.messageService.notifyConversationListUpdated({
          peerId: data?.sender?.id ?? data?.sender ?? null,
          message: data?.message ?? "",
          updated_at: data?.updated_at ?? new Date().toISOString(),
          is_read: 0,
        });
        this.messageService.setUnreadMessages();
        break;

      default:
        break;
    }
  }
  AcceptMeetingResponseObservable() {
    return this.dispatcherMeetingResponseSubject.asObservable();
  }
  resetMeetingResponse() {
    this.dispatcherMeetingResponseSubject.next([]);
  }
  resetMeetingDemand() {
    this.dispatcherMeetingDemandeSubject.next([]);
  }

  AcceptMeetingDemandObservable() {
    return this.dispatcherMeetingDemandeSubject.asObservable();
  }
  AcceptDemandeObservable() {
    return this.dispatcherAcceptDemandeSubject.asObservable();
  }
  NewNotificationObservable() {
    return this.dispatcherNewNotificationSubject.asObservable();
  }
  DestinationAttentObservable() {
    return this.dispatcherDestinationAttentSubject.asObservable();
  }
  ConfirmProviderArrivalObservable(): Observable<any> {
    return this.dispatcherConfirmProviderArrivalSubject.asObservable();
  }
  OnraodDemandeCanceledObservable() {
    return this.onraodDemandeCanceled.asObservable();
  }
  ProviersPositionsObservable() {
    return this.dispatcherProviersPositions.asObservable();
  }
  DevisCreatedObservabe() {
    return this.dispatcherDevisCreated.asObservable();
  }
  resetDevisCreated() {
    this.dispatcherDevisCreated.next(null);
  }
  DevisStatusChangedObservable() {
    return this.dispatcherDevisStatusChanged.asObservable();
  }
  resetStatusChanged() {
    this.dispatcherDevisStatusChanged.next(null);
  }
  ImagePreuveUploadedObservable() {
    return this.dispatcherImagePreuveUploaded.asObservable();
  }
  ProviderApplyinDevisObservable() {
    return this.dispatcherProviderApplyinDevis.asObservable();
  }
  DispatcherClientAttentPendingObservabe() {
    return this.dispatcherClientAttentPending.asObservable();
  }
  DispatcherDemandeCancelledObservable() {
    return this.dispatcherDemandeCancelled.asObservable();
  }
  DispatcherSignlerProblemObservable() {
    return this.dispatcherSignlerProblem.asObservable();
  }
  dispatcherUserUpdatedObservable() {
    return this.dispatcherUserUpdated.asObservable();
  }
  dispatcherDemandeStatusUpdatedObservable() {
    return this.dispatcherDemandeStatusUpdated.asObservable();
  }
  dispatcherSignlerProblemProviderObservable() {
    return this.dispatcherSignlerProblemProvider.asObservable();
  }
  dispatcherAdminCancelDemandeObservable() {
    return this.dispatcherAdminCancelDemande.asObservable();
  }
  dispatcherReturnOnboardingObservable() {
    return this.dispatcherReturnOnboarding.asObservable();
  }
  demandesDistanceObservable() {
    return this.demandesDistance.asObservable();
  }
  popPreAssignedCommandObservable() {
    return this.popPreAssignedCommand.asObservable();
  }
  clientDemandeChangesObservable() {
    return this.clientDemandeChanges.asObservable();
  }
  transporterStatusObservable() {
    return this.dispatcherTransporterStatus.asObservable();
  }

  ngOnDestroy(): void {
    // Clean up listeners
    if (this.appStateListener) {
      this.appStateListener.remove();
      this.appStateListener = null;
    }

    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
      this.networkSubscription = null;
    }

    if (this.nativePusherEventSubscription) {
      this.nativePusherEventSubscription.unsubscribe();
      this.nativePusherEventSubscription = null;
    }

    if (this.nativeEventProcessedSubscription) {
      this.nativeEventProcessedSubscription.unsubscribe();
      this.nativeEventProcessedSubscription = null;
    }

    if (this.reconnectDebounceTimeout) {
      clearTimeout(this.reconnectDebounceTimeout);
      this.reconnectDebounceTimeout = null;
    }

    if (this.foregroundHealDebounceTimeout) {
      clearTimeout(this.foregroundHealDebounceTimeout);
      this.foregroundHealDebounceTimeout = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    // Unsubscribe from channels
    this.unsubscribeChannels();

    // Stop native background Pusher listening
    if (Capacitor.isNativePlatform() && this.pusherBackgroundService.isAvailable()) {
      this.pusherBackgroundService.stopListening().catch((error) => {
        console.warn('[PusherService] Failed to stop native background listening:', error);
      });
    }
  }
}
